// BLE remote contract v1, shared with the iOS app and Unity.
// See docs/vstbalance-remote-integration-plan.md §4. Native sends these as a JSON string to window.onRemoteEvent.

export const REMOTE_CONTRACT_VERSION = 1;

// Semantic button names. These match Unity's ControllerInput.ButtonType exactly.
export enum RemoteButton {
  UP = "UP",
  DOWN = "DOWN",
  OK = "OK",
  START_STOP = "START_STOP",
  REWIND = "REWIND",
  NEXT = "NEXT",
}

export interface RemoteButtonEvent {
  v: number;
  type: "button";
  button: RemoteButton;
  // Physical button number 1-6, for logs/diagnostics only. Act on `button`.
  buttonId: number;
  // Increases per press for the app's lifetime; used to drop duplicates.
  seq: number;
  // Unix epoch milliseconds from native.
  ts: number;
}

export interface RemoteStatusEvent {
  v: number;
  type: "status";
  bluetooth: "poweredOn" | "poweredOff" | "unauthorized" | "unsupported" | "unknown";
  paired: boolean;
  connected: boolean;
  remoteId?: string;
  name?: string;
  battery?: number;
}

export interface RemoteScanEntry {
  remoteId: string;
  name?: string;
  rssi: number;
}

export interface RemoteScanResultEvent {
  v: number;
  type: "scanResult";
  remotes: RemoteScanEntry[];
}

export type RemoteEvent = RemoteButtonEvent | RemoteStatusEvent | RemoteScanResultEvent;

const BUTTONS = new Set<string>(Object.values(RemoteButton));

// Parses and validates a message from native. Returns null for anything malformed or of an unknown type,
// so newer native builds can add message types without breaking older web builds. Unknown fields are ignored.
export function parseRemoteEvent(json: string): RemoteEvent | null {
  let data: any;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object" || typeof data.v !== "number" || data.v < 1) {
    return null;
  }

  switch (data.type) {
    case "button":
      if (!BUTTONS.has(data.button) || typeof data.seq !== "number" || typeof data.buttonId !== "number") {
        return null;
      }
      return {
        v: data.v,
        type: "button",
        button: data.button as RemoteButton,
        buttonId: data.buttonId,
        seq: data.seq,
        ts: typeof data.ts === "number" ? data.ts : Date.now(),
      };

    case "status":
      if (typeof data.paired !== "boolean" || typeof data.connected !== "boolean") {
        return null;
      }
      return {
        v: data.v,
        type: "status",
        bluetooth: typeof data.bluetooth === "string" ? data.bluetooth : "unknown",
        paired: data.paired,
        connected: data.connected,
        remoteId: typeof data.remoteId === "string" ? data.remoteId : undefined,
        name: typeof data.name === "string" ? data.name : undefined,
        battery: typeof data.battery === "number" ? data.battery : undefined,
      };

    case "scanResult":
      if (!Array.isArray(data.remotes)) {
        return null;
      }
      return {
        v: data.v,
        type: "scanResult",
        remotes: data.remotes
          .filter((r: any) => r && typeof r.remoteId === "string" && typeof r.rssi === "number")
          .map((r: any) => ({ remoteId: r.remoteId, name: typeof r.name === "string" ? r.name : undefined, rssi: r.rssi })),
      };

    default:
      return null;
  }
}
